/**
 * cardHelpers
 * Shared helpers, badges, avatars, format helpers, and small components
 * used by multiple chat data card modules.
 */

import type { LeagueKey } from '@parlay-picker/shared/league';
import { orderSeasonStats } from '@parlay-picker/shared/statLabels';
import { findTeam } from '@parlay-picker/shared/teams';
import { usePlayerHeadshotUrl } from '@views/picks/utils';
import { User } from 'lucide-react';
import { type ReactElement, useState } from 'react';

// =============================================================================
// Verdict / Status Helpers
// =============================================================================

export const verdictColors: Record<string, string> = {
  STRONG: 'bg-theme-success/10 text-theme-success',
  OKAY: 'bg-theme-primary/10 text-theme-primary',
  RISKY: 'bg-theme-warning/10 text-theme-warning',
  AVOID: 'bg-theme-danger/10 text-theme-danger',
};

export const resultColors: Record<string, string> = {
  won: 'text-theme-success',
  lost: 'text-theme-danger',
  pending: 'text-theme-warning',
  push: 'text-theme-text-secondary',
};

export function VerdictBadge({ verdict }: { verdict: string }): ReactElement {
  const cls = verdictColors[verdict] || 'bg-theme-bg text-theme-text-secondary';
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${cls}`}>{verdict}</span>
  );
}

export function ResultBadge({ result }: { result: string }): ReactElement {
  const color = resultColors[result] || 'text-theme-text-secondary';
  return <span className={`text-xs font-semibold capitalize ${color}`}>{result}</span>;
}

// =============================================================================
// Shared Avatar + Team Badge Helper
// =============================================================================

export function AvatarWithBadge({
  headshotUrl,
  teamLogo,
  size = 'md',
}: {
  headshotUrl: string | null;
  teamLogo?: string;
  size?: 'sm' | 'md';
}): ReactElement {
  const sz = size === 'sm' ? 'w-6 h-6' : 'w-8 h-8';
  const badgeSz = size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5';
  return (
    <div className={`relative shrink-0 ${sz} mt-0.5`}>
      {headshotUrl ? (
        <img
          src={headshotUrl}
          alt=""
          className={`${sz} rounded-full object-cover bg-theme-surface-hover`}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
            const fallback = (e.target as HTMLElement).nextElementSibling;
            if (fallback) (fallback as HTMLElement).style.display = 'flex';
          }}
        />
      ) : null}
      <div
        className={`${sz} rounded-full bg-theme-surface-hover flex items-center justify-center`}
        style={{ display: headshotUrl ? 'none' : 'flex' }}
      >
        <User className="w-4 h-4 text-theme-text-tertiary" />
      </div>
      {teamLogo && (
        <img
          src={teamLogo}
          alt=""
          className={`absolute -bottom-0.5 -right-0.5 ${badgeSz} rounded-full bg-theme-surface border border-theme-border`}
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none';
          }}
        />
      )}
    </div>
  );
}

// =============================================================================
// Player Stats Header (with headshot + team badge)
// =============================================================================

export function PlayerStatsHeader({
  name,
  team,
  leagueKey,
}: {
  name: string;
  team: string;
  leagueKey?: LeagueKey;
}): ReactElement {
  const headshotUrl = usePlayerHeadshotUrl(name, leagueKey ?? null);
  const teamInfo = leagueKey ? findTeam(team, leagueKey) : null;
  return (
    <div className="flex items-center gap-2">
      <AvatarWithBadge headshotUrl={headshotUrl} teamLogo={teamInfo?.logo} />
      <div className="min-w-0">
        <span className="text-sm font-semibold text-theme-text block truncate">{name}</span>
        {team && <span className="text-[10px] text-theme-text-secondary">{team}</span>}
      </div>
    </div>
  );
}

// =============================================================================
// StatCell
// =============================================================================

export function StatCell({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: string;
}): ReactElement {
  return (
    <div className="text-center">
      <div className="text-[10px] text-theme-text-tertiary">{label}</div>
      <div className={`text-sm font-medium ${color || 'text-theme-text'}`}>{value}</div>
    </div>
  );
}

// =============================================================================
// ExpandableText
// =============================================================================

export const LINE_CLAMP_MAP: Record<number, string> = {
  1: 'line-clamp-1',
  2: 'line-clamp-2',
  3: 'line-clamp-3',
  4: 'line-clamp-4',
};

/** Clickable text that toggles between line-clamped and fully expanded. */
export function ExpandableText({
  text,
  className,
  lineClamp,
}: {
  text: string;
  className?: string;
  lineClamp: number;
}): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const clampClass = LINE_CLAMP_MAP[lineClamp] || 'line-clamp-2';
  return (
    <p
      className={`${className || ''} cursor-pointer ${expanded ? '' : clampClass}`}
      onClick={() => setExpanded(!expanded)}
      title="Click to expand"
    >
      {text}
    </p>
  );
}

// =============================================================================
// Confidence helpers
// =============================================================================

export const confidenceStyles: Record<string, string> = {
  High: 'bg-theme-success/15 text-theme-success',
  Medium: 'bg-theme-warning/15 text-theme-warning',
  Low: 'bg-orange-400/15 text-orange-400',
};

export const confidenceDot: Record<string, string> = {
  High: 'bg-theme-success',
  Medium: 'bg-theme-warning',
  Low: 'bg-orange-400',
};

// =============================================================================
// Format helpers
// =============================================================================

export function formatLabel(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Extract displayable stat entries from a stats object, filtering out non-numeric noise */
export function formatStatEntries(stats: Record<string, unknown>): Array<[string, string]> {
  const statKeyMap: Record<string, string> = {
    pts: 'PTS',
    reb: 'REB',
    ast: 'AST',
    stl: 'STL',
    blk: 'BLK',
    fg3m: '3PM',
    fgm: 'FGM',
    fga: 'FGA',
    ftm: 'FTM',
    fta: 'FTA',
    turnover: 'TO',
    min: 'MIN',
  };

  return orderSeasonStats(stats)
    .filter(([key, val]) => {
      if (val == null || typeof val === 'object') return false;
      // Prefer mapped stat keys, but also show any numeric value
      return statKeyMap[key] || typeof val === 'number';
    })
    .slice(0, 9)
    .map(([key, val]) => [
      statKeyMap[key] || formatLabel(key),
      typeof val === 'number'
        ? Number.isInteger(val)
          ? String(val)
          : val.toFixed(1)
        : String(val),
    ]);
}
